<template>
    <div class="attachment-actions" :class="[`attachment-actions--${layout}`, { 'attachment-actions--unnamed': !showName }]">
        <span
            v-if="showName"
            class="attachment-actions-name"
            :class="{ 'attachment-actions-name-previewable': canPreview }"
            :title="fileName"
            @click="handleNameClick"
        >{{ fileName }}</span>
        <div class="attachment-actions-buttons">
            <el-button
                class="attachment-actions-button"
                type="text"
                :loading="previewLoading"
                :disabled="disabled || previewLoading || downloadLoading"
                @click.stop="handlePreview"
            >
                <i v-if="!previewLoading" class="el-icon-view"></i>
                <span>{{ previewLoading ? '加载中' : '预览' }}</span>
            </el-button>
            <el-button
                v-if="showDownload"
                class="attachment-actions-button"
                type="text"
                :loading="downloadLoading"
                :disabled="disabled || previewLoading || downloadLoading"
                @click.stop="handleDownload"
            >
                <i v-if="!downloadLoading" class="el-icon-download"></i>
                <span>{{ downloadLoading ? '下载中' : '下载' }}</span>
            </el-button>
        </div>

        <el-image-viewer
            v-if="imageViewerVisible"
            :url-list="imageViewerUrls"
            :z-index="imageViewerZIndex"
            :on-close="closeImageViewer"
        />
        <pdf-preview :visible.sync="pdfPreviewVisible" :file="pdfPreviewFile" />
        <office-preview v-if="enableOfficePreview && officePreviewVisible" :visible.sync="officePreviewVisible" :file="officePreviewFile" />
        <el-dialog v-if="unsupportedPreviewVisible" :visible.sync="unsupportedPreviewVisible" :title="fileName" width="520px" custom-class="attachment-preview-unavailable-dialog" append-to-body :close-on-click-modal="false">
            <p>该文件格式暂不支持在线预览，请下载后查看。</p>
            <span slot="footer"><el-button @click="unsupportedPreviewVisible = false">关闭</el-button><el-button v-if="showDownload" type="primary" icon="el-icon-download" :loading="downloadLoading" @click="handleDownload">下载</el-button></span>
        </el-dialog>
    </div>
</template>

<script>
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'
import { PopupManager } from 'element-ui/lib/utils/popup'
import PdfPreview from '@/components/PdfPreview.vue'
import {
    canPreviewAttachment,
    downloadAttachment,
    fetchAttachmentBlobUrl,
    getAttachmentUrl,
    isImageAttachment,
    isPdfAttachment,
    getOfficeAttachmentType,
} from '@/utils/attachment'

export default {
    name: 'AttachmentActions',
    components: {
        ElImageViewer,
        PdfPreview,
        OfficePreview: () => import('@/components/OfficePreview.vue'),
    },
    props: {
        enableOfficePreview: {
            type: Boolean,
            default: true,
        },
        file: {
            required: true,
        },
        fileList: {
            type: Array,
            default: () => [],
        },
        showName: {
            type: Boolean,
            default: true,
        },
        showDownload: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        layout: {
            type: String,
            default: 'inline',
            validator: (value) => ['inline', 'compact', 'list'].includes(value),
        },
    },
    data() {
        return {
            previewLoading: false,
            downloadLoading: false,
            imageViewerVisible: false,
            imageViewerUrls: [],
            imageViewerZIndex: 2000,
            pdfPreviewVisible: false,
            pdfPreviewFile: null,
            officePreviewVisible: false,
            officePreviewFile: null,
            unsupportedPreviewVisible: false,
        }
    },
    computed: {
        fileName() {
            return this.file.original_name || this.file.name || this.file.file_name || '附件'
        },
        canPreview() {
            return canPreviewAttachment(this.file) || (this.enableOfficePreview && !!getOfficeAttachmentType(this.file))
        },
    },
    beforeDestroy() {
        this.revokeImageViewerUrls()
    },
    methods: {
        handleNameClick() {
            if (this.canPreview) this.handlePreview()
        },
        async handlePreview() {
            if (this.disabled || this.previewLoading || this.downloadLoading) return
            if (!this.canPreview) {
                this.unsupportedPreviewVisible = true
                return
            }

            if (this.enableOfficePreview && getOfficeAttachmentType(this.file)) {
                this.officePreviewFile = { ...this.toPdfPreviewFile(this.file), type: getOfficeAttachmentType(this.file) }
                this.officePreviewVisible = true
                return
            }

            if (isPdfAttachment(this.file)) {
                this.pdfPreviewFile = this.toPdfPreviewFile(this.file)
                this.pdfPreviewVisible = true
                return
            }

            if (!isImageAttachment(this.file)) return
            this.previewLoading = true
            try {
                const imageFiles = (this.fileList.length ? this.fileList : [this.file]).filter((item) => isImageAttachment(item))
                const orderedFiles = [this.file].concat(imageFiles.filter((item) => item !== this.file && this.getFileKey(item) !== this.getFileKey(this.file)))
                const urls = await Promise.all(orderedFiles.map((item) => this.getPreviewUrl(item)))
                this.revokeImageViewerUrls()
                this.imageViewerUrls = urls
                // 源码版图片预览与全局 Element UI 弹窗的层级计数器不同，需显式取全局下一层级。
                this.imageViewerZIndex = PopupManager.nextZIndex()
                this.imageViewerVisible = true
            } catch (error) {
                alertErr((error && error.msg) || '附件预览失败')
            } finally {
                this.previewLoading = false
            }
        },
        async handleDownload() {
            if (this.disabled || this.previewLoading || this.downloadLoading) return

            this.downloadLoading = true
            try {
                const blob = this.getLocalBlob(this.file)
                if (blob) {
                    this.downloadBlob(blob)
                } else {
                    const url = getAttachmentUrl(this.file)
                    if (!url) throw { msg: '暂无可下载附件' }
                    await downloadAttachment(url, this.fileName)
                }
            } catch (error) {
                alertErr((error && error.msg) || '附件下载失败')
            } finally {
                this.downloadLoading = false
            }
        },
        getPreviewUrl(file) {
            const blob = this.getLocalBlob(file)
            if (blob) return Promise.resolve(window.URL.createObjectURL(blob))
            const url = getAttachmentUrl(file)
            if (!url) return Promise.reject({ msg: '暂无可预览附件' })
            return fetchAttachmentBlobUrl(url)
        },
        toPdfPreviewFile(file) {
            const blob = this.getLocalBlob(file)
            return {
                name: file.original_name || file.name || file.file_name || 'PDF预览',
                url: blob ? '' : getAttachmentUrl(file),
                blob,
            }
        },
        getLocalBlob(file) {
            if (file instanceof Blob) return file
            return file && file.blob instanceof Blob ? file.blob : null
        },
        getFileKey(file) {
            return file.id || getAttachmentUrl(file) || file.name || ''
        },
        downloadBlob(blob) {
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = this.fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
        },
        closeImageViewer() {
            this.imageViewerVisible = false
            this.revokeImageViewerUrls()
        },
        revokeImageViewerUrls() {
            this.imageViewerUrls.forEach((url) => window.URL.revokeObjectURL(url))
            this.imageViewerUrls = []
        },
    },
}
</script>

<style lang="less" scoped>
.attachment-actions {
    box-sizing: border-box;
    display: flex;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 4px 10px;
    vertical-align: middle;
    text-align: left;
}

.attachment-actions--unnamed { width: auto; }

.attachment-actions.attachment-actions {
    display: flex;
    overflow: visible;
    white-space: normal;
}

.attachment-actions-name {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    color: #606266;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-actions-name-previewable {
    color: #2266ff;
    cursor: pointer;
}

.attachment-actions-name-previewable:hover {
    text-decoration: underline;
}

.attachment-actions-buttons {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin: 0;
}

.attachment-actions-button {
    min-width: 44px;
    font-size: inherit;
    margin: 0 !important;
    padding: 0;
    line-height: 24px;
}

.attachment-actions-button i {
    margin-right: 0;
}

.attachment-actions-button /deep/ [class*="el-icon-"] + span {
    margin-left: 3px;
}

.attachment-actions--compact {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    text-align: left;
}

.attachment-actions--compact .attachment-actions-name {
    width: 100%;
    flex: none;
    line-height: 20px;
}

.attachment-actions--compact .attachment-actions-buttons {
    justify-content: flex-start;
    gap: 10px;
    margin: 2px 0 0;
}

.attachment-actions--compact .attachment-actions-button {
    min-width: auto;
    line-height: 20px;
}

.attachment-actions--list {
    box-sizing: border-box;
    width: 100%;
    min-height: 40px;
    padding: 7px 10px;
    flex-wrap: nowrap;
    gap: 12px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #f7f8fa;
    transition: border-color 0.2s, background-color 0.2s;
}

.attachment-actions--list:hover {
    border-color: #c6d5ff;
    background: #f5f8ff;
}

.attachment-actions--list .attachment-actions-name {
    flex-basis: auto;
}

.attachment-actions--list .attachment-actions-buttons {
    margin: 0;
}
</style>
<style>
.attachment-preview-unavailable-dialog { max-width: calc(100vw - 32px); }
.attachment-preview-unavailable-dialog .el-dialog__title { display: block; padding-right: 24px; overflow-wrap: anywhere; }
.attachment-preview-unavailable-dialog .el-dialog__body { text-align: left; overflow-wrap: anywhere; }
</style>
